import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, AlertCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { initializeStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PamoyaLogo } from "@/components/logos/PamoyaLogo";

const ADMIN_ROLES = ["super_admin", "admin", "administrator"] as const;

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setLoading(true);

    const { data: authData, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password: password.trim(),
    });

    if (error) {
      setLoading(false);
      setErrorMsg(translateError(error.message));
      return;
    }

    const userId = authData.session?.user?.id;
    if (!userId) {
      setLoading(false);
      setErrorMsg("Impossible de récupérer l'identifiant utilisateur.");
      return;
    }

    const { data: roleData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .in("role", [...ADMIN_ROLES])
      .limit(1);

    if (!roleData || roleData.length === 0) {
      await supabase.auth.signOut();
      setLoading(false);
      setErrorMsg("Accès refusé : ce compte n'a pas les droits administrateur.");
      return;
    }

    // Précharge le catalogue pendant la navigation : ProtectedRoute attendra
    // la même promesse (loadAllFromSQL est dédupliquée) au lieu de relancer
    // ~20 requêtes SQL une deuxième fois.
    void initializeStore();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-dvh bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-sm">

        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <PamoyaLogo className="h-12 mb-4" />
          <h1 className="text-2xl font-black tracking-tight text-center">Pamoya Admin</h1>
          <p className="text-sm text-muted-foreground mt-1 text-center">
            Connexion réservée aux administrateurs
          </p>
        </div>

        {/* Card */}
        <div className="bg-card border border-border rounded-3xl p-6 shadow-sm">
          {/* Hint */}
          <div className="bg-brand-accent-light border border-brand-accent/20 rounded-xl px-4 py-3 mb-6">
            <p className="text-xs text-muted-foreground font-medium leading-relaxed">
              Accès owner : utilisez le compte email associé au rôle <span className="text-brand-accent font-bold">super_admin</span> ou <span className="text-brand-accent font-bold">administrator</span> dans la base de données.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground ml-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setErrorMsg(""); }}
                  placeholder="admin@pamoya.org"
                  autoComplete="email"
                  autoFocus
                  required
                  className="pl-11 h-14 rounded-2xl bg-secondary"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground ml-1">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => { setPassword(e.target.value); setErrorMsg(""); }}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  className="pl-11 pr-11 h-14 rounded-2xl bg-secondary"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {errorMsg && (
              <div className="flex items-start gap-2.5 bg-destructive/10 border border-destructive/20 rounded-2xl px-4 py-3">
                <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-xs font-semibold text-destructive leading-relaxed">{errorMsg}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 rounded-full bg-brand-accent text-white font-black text-base shadow-[0_8px_24px_rgba(255,61,0,0.35)] hover:bg-brand-accent-dark"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  Vérification...
                </span>
              ) : (
                "Se connecter"
              )}
            </Button>
          </form>
        </div>

        <p className="mt-8 text-center text-xs text-muted-foreground/60">
          Pamoya Admin © 2026
        </p>
      </div>
    </div>
  );
}

function translateError(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes("invalid login credentials")) return "Email ou mot de passe incorrect.";
  if (m.includes("email not confirmed")) return "Email non confirmé. Vérifiez votre boîte mail.";
  if (m.includes("rate limit")) return "Trop de tentatives. Patientez quelques minutes.";
  if (m.includes("network") || m.includes("failed to fetch")) return "Problème de connexion réseau.";
  if (m.includes("session expired")) return "Session expirée. Veuillez vous reconnecter.";
  return "Une erreur est survenue. Veuillez réessayer.";
}
